/**
 * @lines98/client/lib/shared-flight — ОБОБЩЁННЫЙ дедуп in-flight промиса
 * (instant-ui-hydration §4): один полёт fetch'а переиспользуется конкурентными
 * вызывающими (boot-префетч + mount экрана), вместо дубль-запроса.
 *
 * ЗАЧЕМ / WHY THIS EXISTS (instant-hydration на лидерборд+профиль 2026-06-10):
 * `services/meta-prefetch` ввёл дедуп `getMetaState` для Мастерской/Профиля; лидерборду
 * нужен ТАКОЙ ЖЕ приём на его страницы (boot-префетч активного борда + mount экрана не
 * должны слать два запроса). Чтобы не копировать `inFlight`-логику, эта фабрика —
 * ЕДИНЫЙ «ствол» дедупа: `createSharedFlight(fetcher)` → `{ shared, reset }`. Ветки
 * (`meta-prefetch`, `leaderboard-prefetch`) — тонкие обёртки поверх неё (модель
 * «ствол → ветки», code-structure §1).
 *
 * **Семантика (одинакова для всех веток, §4):** пока полёт «в воздухе» — `shared()`
 * отдаёт ТОТ ЖЕ промис (без второго fetch). После ресолва (успех ИЛИ ошибка) in-flight
 * очищается → следующий `shared()` (ресинк после мутации / повторный заход) летит
 * ЗАНОВО (server-truth не залипает на старом полёте). Идемпотентно от network-perspective
 * для GET'ов (без мутации) — это просто «один полёт вместо параллельных дублей».
 *
 * **Параметризованный ключ (для лидерборда).** Борд имеет РАЗНЫЕ полёты на разные
 * вкладки/режимы (Сегодня/Вчера/каждый режим рекордов) — дедуп держится ПО КЛЮЧУ:
 * `shared(key)` дедупит только одинаковые ключи. Мета — один полёт, ключ-дефолт.
 *
 * **Server-truth цел:** фабрика лишь дедупит ПАРАЛЛЕЛЬНЫЕ вызовы того же легального
 * запроса — раньше во времени/без дублей, не «новый источник». Анти-чит/движок не
 * затронуты: ничего не верифицирует и не пишет.
 *
 * Cross-references:
 *   - services/meta-prefetch.ts (ветка: дедуп + boot-прогрев `GET /meta/state`).
 *   - services/leaderboard-prefetch.ts (ветка: дедуп + boot-прогрев активной страницы борда).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §4 (boot-prefetch + дедуп in-flight).
 */

/** Хендл дедупа: `shared(key?)` отдаёт/запускает полёт; `reset()` — test-seam сброса. */
export interface SharedFlight<T, K = string> {
  /**
   * Запустить `fetcher(key)` ИЛИ переиспользовать уже летящий полёт того же `key` (дедуп).
   * После ресолва (успех/ошибка) запись по `key` очищается → следующий вызов летит заново.
   * `key` опционален (по умолчанию единый — для one-flight веток вроде меты).
   */
  shared(key?: K): Promise<T>;
  /** Сбросить ВСЕ in-flight записи (test-seam; не трогает сеть/снимок). */
  reset(): void;
}

/**
 * Создать дедуп-обёртку вокруг `fetcher`. `fetcher(key)` — функция реального запроса
 * (напр. `() => getMetaState()` или `(k) => getDailyLeaderboard({...})`). Возвращает
 * `{ shared, reset }`.
 *
 * @param fetcher — выполняет реальный сетевой запрос для данного `key`.
 */
export function createSharedFlight<T, K = string>(
  fetcher: (key: K) => Promise<T>,
): SharedFlight<T, K> {
  // Единый «слот» по умолчанию для веток без явного ключа (мета — один полёт).
  const DEFAULT_KEY = '__default__' as unknown as K;
  // Незавершённые полёты по ключу (один на ключ).
  const inFlight = new Map<K, Promise<T>>();

  return {
    shared(key: K = DEFAULT_KEY): Promise<T> {
      const existing = inFlight.get(key);
      if (existing) return existing;
      const flight = fetcher(key);
      inFlight.set(key, flight);
      // Очистить ПОСЛЕ ресолва (успех/ошибка), но только если это всё ещё ТОТ полёт
      // (не затереть запись более позднего полёта того же ключа).
      void flight.finally(() => {
        if (inFlight.get(key) === flight) inFlight.delete(key);
      });
      return flight;
    },

    reset(): void {
      inFlight.clear();
    },
  };
}
